import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Input, Select, Tag, Avatar, Space, Typography,
  Card, Modal, Form, DatePicker, InputNumber, Spin, Badge,
  Timeline, Empty, Row, Col, Tooltip, message, Divider,
} from 'antd';
import {
  FireOutlined, ClockCircleOutlined, PhoneOutlined, UserOutlined,
  SearchOutlined, ReloadOutlined, HistoryOutlined, FileTextOutlined,
  CalendarOutlined, StarFilled, ContactsOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getHotLeadsApi, saveCallFeedbackApi, getCallHistoryApi,
  getFollowUpsApi, getLeadDetailApi,
} from '../../api/crm';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const PRIMARY = '#1a6b2a';

const CALL_STATUS_OPTIONS = [
  { value: 'interested',      label: 'Interested'      },
  { value: 'not_interested',  label: 'Not Interested'  },
  { value: 'call_back_later', label: 'Call Back Later' },
  { value: 'converted',       label: 'Converted'       },
];

const CALL_REASON_OPTIONS = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'information',  label: 'Information'  },
  { value: 'feedback',     label: 'Feedback'     },
  { value: 'support',      label: 'Support'      },
  { value: 'follow_up',    label: 'Follow Up'    },
];

const STATUS_COLOR = {
  interested:      'green',
  not_interested:  'red',
  call_back_later: 'orange',
  converted:       'blue',
};

// ── Call Modal ───────────────────────────────────────────────────────────────

function CallModal({ open, lead, onClose, onSuccess }) {
  const [form]   = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await saveCallFeedbackApi({
        user_id:       lead.id,
        call_type:     'outbound',
        call_status:   values.call_status,
        call_reason:   values.call_reason,
        call_duration: values.call_duration ?? null,
        notes:         values.notes         ?? null,
        next_call_at:  values.next_call_at ? values.next_call_at.toISOString() : null,
        lead_score:    values.lead_score    ?? null,
      });
      message.success('Call feedback saved');
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        <Space>
          <PhoneOutlined style={{ color: PRIMARY }} />
          <span>Call Feedback</span>
          {lead && (
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              — {lead.name} ({lead.mobile})
            </Text>
          )}
        </Space>
      }
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Save Feedback"
      okButtonProps={{ loading: saving, style: { background: PRIMARY, borderColor: PRIMARY } }}
      width={540}
      destroyOnClose
    >
      {lead && (
        <div style={{
          background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
          padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 12,
        }}>
          <Avatar icon={<UserOutlined />} style={{ background: PRIMARY, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600 }}>{lead.name}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {lead.mobile} · {[lead.city, lead.state].filter(Boolean).join(', ')}
            </div>
            <Space style={{ marginTop: 4 }}>
              <Tag color="blue" style={{ fontSize: 11 }}>{lead.crop_count ?? 0} Crops</Tag>
              <Tag color={(lead.expense_count ?? 0) > 5 ? 'red' : 'default'} style={{ fontSize: 11 }}>
                {lead.expense_count ?? 0} Expenses
              </Tag>
            </Space>
          </div>
        </div>
      )}

      <Form form={form} layout="vertical" size="middle">
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="call_status" label="Call Status" rules={[{ required: true, message: 'Required' }]}>
              <Select options={CALL_STATUS_OPTIONS} placeholder="Select status" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="call_reason" label="Call Reason" rules={[{ required: true, message: 'Required' }]}>
              <Select options={CALL_REASON_OPTIONS} placeholder="Select reason" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="call_duration" label="Call Duration (sec)">
              <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 120" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="lead_score" label="Lead Score (1–10)">
              <InputNumber min={1} max={10} style={{ width: '100%' }} placeholder="1–10" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="next_call_at" label="Next Call At">
          <DatePicker
            showTime
            format="DD MMM YYYY HH:mm"
            style={{ width: '100%' }}
            disabledDate={(d) => d && d < dayjs().startOf('day')}
          />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Notes about the call..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Hot Leads View ───────────────────────────────────────────────────────────

function HotLeadsView({ onCall, onViewDetail }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [pageSize,setPageSize]= useState(20);
  const [total,   setTotal]   = useState(0);

  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter,  setCityFilter]  = useState('');
  const [minCrops,    setMinCrops]    = useState('');
  const [minExpenses, setMinExpenses] = useState('');
  const [activeDays,  setActiveDays]  = useState('');

  const debounceRef = useRef(null);

  // Pass page + pageSize explicitly to avoid stale closure issues
  const doFetch = useCallback(async (pg, ps, filters) => {
    setLoading(true);
    try {
      const res = await getHotLeadsApi({
        page:         pg,
        per_page:     ps,
        state:        filters.state        || undefined,
        city:         filters.city         || undefined,
        min_crops:    filters.minCrops     || undefined,
        min_expenses: filters.minExpenses  || undefined,
        active_days:  filters.activeDays   || undefined,
      });
      const d = res.data?.data || res.data || {};
      setData(Array.isArray(d.data) ? d.data : []);
      setTotal(d.total ?? 0);
      setPage(d.current_page ?? pg);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const currentFilters = useRef({ state: '', city: '', minCrops: '', minExpenses: '', activeDays: '' });

  useEffect(() => {
    currentFilters.current = { state: stateFilter, city: cityFilter, minCrops, minExpenses, activeDays };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      doFetch(1, pageSize, currentFilters.current);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, cityFilter, minCrops, minExpenses, activeDays]);

  // Initial load
  useEffect(() => {
    doFetch(1, pageSize, currentFilters.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pg, ps) => {
    setPage(pg);
    setPageSize(ps);
    doFetch(pg, ps, currentFilters.current);
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      width: 170,
      render: (_, r) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {r.expense_count > 5 && (
              <Tooltip title="Hot Lead">
                <FireOutlined style={{ color: '#ff4d4f', fontSize: 13 }} />
              </Tooltip>
            )}
            <Button
              type="link"
              style={{ padding: 0, fontWeight: 600, color: PRIMARY, textAlign: 'left', height: 'auto' }}
              onClick={() => onViewDetail(r)}
            >
              {r.name}
            </Button>
          </div>
          <div style={{ fontSize: 11, color: '#aaa' }}>#{r.id}</div>
        </div>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      width: 125,
      render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{v || '—'}</Text>,
    },
    {
      title: 'State / City',
      key: 'location',
      width: 140,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.state || '—'}</div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.city || ''}</div>
        </div>
      ),
    },
    {
      title: 'Crops',
      dataIndex: 'crop_count',
      width: 75,
      sorter: (a, b) => (a.crop_count ?? 0) - (b.crop_count ?? 0),
      render: (v) => <Tag color="green">{v ?? 0}</Tag>,
    },
    {
      title: 'Expenses',
      dataIndex: 'expense_count',
      width: 90,
      sorter: (a, b) => (a.expense_count ?? 0) - (b.expense_count ?? 0),
      render: (v) => (
        <Tag color={v > 5 ? 'red' : 'default'}>
          {v > 5 && <FireOutlined style={{ marginRight: 3 }} />}{v ?? 0}
        </Tag>
      ),
    },
    {
      title: 'Last Active',
      dataIndex: 'last_open_at',
      width: 120,
      sorter: (a, b) => dayjs(a.last_open_at).unix() - dayjs(b.last_open_at).unix(),
      render: (v) => v ? (
        <Tooltip title={dayjs(v).format('DD MMM YYYY HH:mm')}>
          <span style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</span>
        </Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, r) => (
        <Button
          type="primary"
          size="small"
          icon={<PhoneOutlined />}
          style={{ background: PRIMARY, borderColor: PRIMARY }}
          onClick={() => onCall(r)}
        >
          Call
        </Button>
      ),
    },
  ];

  const hasFilters = stateFilter || cityFilter || minCrops || minExpenses || activeDays;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0 }}>
          <FireOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
          Hot Leads
          {total > 0 && <Tag style={{ marginLeft: 8, fontWeight: 400 }}>{total} total</Tag>}
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => doFetch(page, pageSize, currentFilters.current)}
          loading={loading}
        />
      </div>

      <Card style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: '12px 14px' } }}>
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="State"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <InputNumber
              placeholder="Min Crops"
              value={minCrops || undefined}
              onChange={(v) => setMinCrops(v ?? '')}
              style={{ width: '100%' }}
              min={0}
              size="small"
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <InputNumber
              placeholder="Min Expenses"
              value={minExpenses || undefined}
              onChange={(v) => setMinExpenses(v ?? '')}
              style={{ width: '100%' }}
              min={0}
              size="small"
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <InputNumber
              placeholder="Active Days"
              value={activeDays || undefined}
              onChange={(v) => setActiveDays(v ?? '')}
              style={{ width: '100%' }}
              min={1}
              size="small"
            />
          </Col>
          {hasFilters && (
            <Col xs={12} sm={6} md={2}>
              <Button size="small" onClick={() => {
                setStateFilter(''); setCityFilter('');
                setMinCrops(''); setMinExpenses(''); setActiveDays('');
              }}>
                Clear
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 750 }}
          onRow={(r) => ({
            style: r.expense_count > 5 ? { background: '#fff7f0' } : {},
          })}
          pagination={{
            current:         page,
            pageSize:        pageSize,
            total:           total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal:       (t, rng) => `${rng[0]}–${rng[1]} of ${t}`,
            onChange:        handleTableChange,
          }}
        />
      </Card>
    </div>
  );
}

// ── Follow Ups View ──────────────────────────────────────────────────────────

function FollowUpsView({ onCall }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [pageSize,setPageSize]= useState(10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFollowUpsApi();
      const raw = res.data?.data || res.data || [];
      setData(Array.isArray(raw) ? raw : []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar icon={<UserOutlined />} style={{ background: PRIMARY, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name || r.user_name || '—'}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{r.mobile || '—'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Next Call',
      dataIndex: 'next_call_at',
      width: 160,
      sorter: (a, b) => dayjs(a.next_call_at).unix() - dayjs(b.next_call_at).unix(),
      render: (v) => v ? (
        <span style={{ color: dayjs(v).isBefore(dayjs()) ? '#ff4d4f' : '#faad14', fontSize: 13 }}>
          <CalendarOutlined style={{ marginRight: 4 }} />
          {dayjs(v).format('DD MMM HH:mm')}
        </span>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      render: (v) => v ? (
        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>{v}</Text>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'action',
      width: 110,
      fixed: 'right',
      render: (_, r) => (
        <Button
          type="primary"
          size="small"
          icon={<PhoneOutlined />}
          style={{ background: PRIMARY, borderColor: PRIMARY }}
          onClick={() => onCall(r)}
        >
          Call Now
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0 }}>
          <ClockCircleOutlined style={{ color: '#faad14', marginRight: 6 }} />
          Follow Ups
          {data.length > 0 && <Badge count={data.length} style={{ background: '#faad14', marginLeft: 8 }} />}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} />
      </div>

      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(r, i) => r.id ?? r.user_id ?? i}
          loading={loading}
          size="small"
          scroll={{ x: 600 }}
          locale={{ emptyText: <Empty description="No follow-ups scheduled" style={{ padding: 32 }} /> }}
          pagination={{
            current:         page,
            pageSize:        pageSize,
            total:           data.length,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal:       (t, rng) => `${rng[0]}–${rng[1]} of ${t}`,
            onChange:        (pg, ps) => { setPage(pg); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
}

// ── Call History View ────────────────────────────────────────────────────────

function CallHistoryView({ lead }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [pageSize,setPageSize]= useState(10);

  useEffect(() => {
    if (!lead?.id) return;
    setData([]);
    setPage(1);
    setLoading(true);
    getCallHistoryApi(lead.id)
      .then((res) => {
        const raw = res.data?.data || res.data || [];
        setData(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lead?.id]);

  if (!lead) {
    return (
      <Empty
        description="Select a lead to view call history"
        style={{ padding: 60 }}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 140,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
      render: (v) => v ? (
        <span style={{ fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY HH:mm')}</span>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'call_status',
      width: 140,
      render: (v) => v ? (
        <Tag color={STATUS_COLOR[v] || 'default'} style={{ textTransform: 'capitalize', fontSize: 11 }}>
          {v.replace(/_/g, ' ')}
        </Tag>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Reason',
      dataIndex: 'call_reason',
      width: 130,
      render: (v) => v ? (
        <Tag style={{ textTransform: 'capitalize', fontSize: 11 }}>
          {v.replace(/_/g, ' ')}
        </Tag>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Duration',
      dataIndex: 'call_duration',
      width: 100,
      render: (v) => v != null ? (
        <span style={{ fontSize: 12 }}>{Math.floor(v / 60)}m {v % 60}s</span>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Score',
      dataIndex: 'lead_score',
      width: 80,
      sorter: (a, b) => (a.lead_score ?? 0) - (b.lead_score ?? 0),
      render: (v) => v ? (
        <Tag color="gold" style={{ fontSize: 11 }}>
          <StarFilled style={{ marginRight: 2 }} />{v}/10
        </Tag>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Next Call',
      dataIndex: 'next_call_at',
      width: 140,
      render: (v) => v ? (
        <span style={{ fontSize: 12, color: '#faad14' }}>
          {dayjs(v).format('DD MMM HH:mm')}
        </span>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      render: (v) => v ? (
        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>{v}</Text>
      ) : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Avatar icon={<UserOutlined />} style={{ background: PRIMARY }} />
        <div>
          <Title level={5} style={{ margin: 0 }}>{lead.name}</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{lead.mobile}</Text>
        </div>
        {data.length > 0 && (
          <Tag style={{ marginLeft: 'auto' }}>{data.length} calls</Tag>
        )}
      </div>

      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(r, i) => r.id ?? i}
          loading={loading}
          size="small"
          scroll={{ x: 800 }}
          locale={{ emptyText: <Empty description="No call history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 32 }} /> }}
          pagination={{
            current:         page,
            pageSize:        pageSize,
            total:           data.length,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal:       (t, rng) => `${rng[0]}–${rng[1]} of ${t}`,
            onChange:        (pg, ps) => { setPage(pg); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
}

// ── Lead Detail View ─────────────────────────────────────────────────────────

function LeadDetailView({ lead, onCall }) {
  const [detail,  setDetail]  = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [histPage,    setHistPage]    = useState(1);
  const [histPageSize,setHistPageSize]= useState(5);

  useEffect(() => {
    if (!lead?.id) return;
    setDetail(null);
    setHistory([]);
    setHistPage(1);
    setLoading(true);
    Promise.allSettled([
      getLeadDetailApi(lead.id),
      getCallHistoryApi(lead.id),
    ]).then(([detailRes, historyRes]) => {
      if (detailRes.status === 'fulfilled') {
        setDetail(detailRes.value.data?.data || detailRes.value.data || null);
      }
      if (historyRes.status === 'fulfilled') {
        const raw = historyRes.value.data?.data || historyRes.value.data || [];
        setHistory(Array.isArray(raw) ? raw : []);
      }
    }).finally(() => setLoading(false));
  }, [lead?.id]);

  if (!lead) {
    return (
      <Empty
        description="Select a lead to view details"
        style={{ padding: 60 }}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  const d        = detail || lead;
  const crops    = d.crops    || [];
  const expenses = d.expenses || [];

  // Paginated history slice for timeline
  const histStart    = (histPage - 1) * histPageSize;
  const histSlice    = history.slice(histStart, histStart + histPageSize);

  return (
    <div>
      <Card style={{ borderRadius: 10, marginBottom: 14 }} styles={{ body: { padding: '14px 16px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar icon={<UserOutlined />} size={48} style={{ background: PRIMARY }} />
            <div>
              <Title level={5} style={{ margin: 0 }}>{d.name || '—'}</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {d.mobile}
                {[d.city, d.state].filter(Boolean).length
                  ? ` · ${[d.city, d.state].filter(Boolean).join(', ')}`
                  : ''}
              </Text>
              <div style={{ marginTop: 6 }}>
                <Space wrap>
                  <Tag color="blue">{d.crop_count ?? crops.length} Crops</Tag>
                  <Tag color={(d.expense_count ?? expenses.length) > 5 ? 'red' : 'default'}>
                    {(d.expense_count ?? expenses.length) > 5 && <FireOutlined style={{ marginRight: 3 }} />}
                    {d.expense_count ?? expenses.length} Expenses
                  </Tag>
                  {d.last_open_at && (
                    <Tag>Active {dayjs(d.last_open_at).fromNow()}</Tag>
                  )}
                </Space>
              </div>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PhoneOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => onCall(lead)}
          >
            Call Now
          </Button>
        </div>
      </Card>

      <Row gutter={[14, 14]}>
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ fontSize: 13 }}>Crops ({crops.length})</span>}
            size="small"
            style={{ borderRadius: 10 }}
            styles={{ body: { padding: '10px 14px', maxHeight: 220, overflowY: 'auto' } }}
          >
            {crops.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No crops" style={{ padding: 12 }} />
            ) : crops.map((c, i) => (
              <div key={c.id || i} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{c.crop_name || c.name || '—'}</div>
                {c.farm_name && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{c.farm_name}</div>}
              </div>
            ))}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={<span style={{ fontSize: 13 }}>Expenses ({expenses.length})</span>}
            size="small"
            style={{ borderRadius: 10 }}
            styles={{ body: { padding: '10px 14px', maxHeight: 220, overflowY: 'auto' } }}
          >
            {expenses.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No expenses" style={{ padding: 12 }} />
            ) : expenses.map((e, i) => (
              <div key={e.id || i} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{e.title || e.expense_type || '—'}</span>
                  {e.amount != null && (
                    <span style={{ color: PRIMARY, fontWeight: 600, fontSize: 13 }}>₹{e.amount}</span>
                  )}
                </div>
                {e.date && (
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>{dayjs(e.date).format('DD MMM YYYY')}</div>
                )}
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* Call History with pagination */}
      <Card
        title={
          <span style={{ fontSize: 13 }}>
            Call History
            {history.length > 0 && (
              <Tag style={{ marginLeft: 8, fontWeight: 400 }}>{history.length} calls</Tag>
            )}
          </span>
        }
        size="small"
        style={{ borderRadius: 10, marginTop: 14 }}
        styles={{ body: { padding: '14px 16px' } }}
      >
        {history.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No calls yet" style={{ padding: 16 }} />
        ) : (
          <>
            <Timeline
              items={histSlice.map((item, idx) => ({
                key: item.id || idx,
                color: STATUS_COLOR[item.call_status] || 'gray',
                children: (
                  <div>
                    <Space wrap style={{ marginBottom: 3 }}>
                      <Tag color={STATUS_COLOR[item.call_status] || 'default'} style={{ fontSize: 11, textTransform: 'capitalize' }}>
                        {item.call_status?.replace(/_/g, ' ')}
                      </Tag>
                      {item.call_reason && (
                        <Tag style={{ fontSize: 11, textTransform: 'capitalize' }}>
                          {item.call_reason.replace(/_/g, ' ')}
                        </Tag>
                      )}
                      {item.lead_score && (
                        <Tag color="gold" style={{ fontSize: 11 }}>
                          <StarFilled style={{ marginRight: 2 }} />{item.lead_score}/10
                        </Tag>
                      )}
                    </Space>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                      {item.created_at ? dayjs(item.created_at).format('DD MMM YYYY HH:mm') : ''}
                      {item.call_duration != null
                        ? ` · ${Math.floor(item.call_duration / 60)}m ${item.call_duration % 60}s`
                        : ''}
                    </div>
                    {item.notes && (
                      <div style={{ fontSize: 12, color: '#555', marginTop: 3, fontStyle: 'italic' }}>
                        "{item.notes}"
                      </div>
                    )}
                    {item.next_call_at && (
                      <div style={{ fontSize: 11, color: '#faad14', marginTop: 3 }}>
                        <CalendarOutlined style={{ marginRight: 3 }} />
                        Next: {dayjs(item.next_call_at).format('DD MMM YYYY HH:mm')}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
            {history.length > histPageSize && (
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {histStart + 1}–{Math.min(histStart + histPageSize, history.length)} of {history.length}
                  </Text>
                  <Button
                    size="small"
                    disabled={histPage === 1}
                    onClick={() => setHistPage((p) => p - 1)}
                  >
                    Prev
                  </Button>
                  <Button
                    size="small"
                    disabled={histStart + histPageSize >= history.length}
                    onClick={() => setHistPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Space>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ── CRM Sub-nav ──────────────────────────────────────────────────────────────

const CRM_NAV = [
  { key: 'hot-leads',    icon: <FireOutlined />,          label: 'Hot Leads'    },
  { key: 'follow-ups',   icon: <ClockCircleOutlined />,   label: 'Follow Ups'   },
  { key: 'call-history', icon: <HistoryOutlined />,        label: 'Call History' },
  { key: 'lead-detail',  icon: <FileTextOutlined />,       label: 'Lead Detail'  },
];

// ── Main Component ───────────────────────────────────────────────────────────

export default function AdminCRM() {
  const [activeView,   setActiveView]   = useState('hot-leads');
  const [selectedLead, setSelectedLead] = useState(null);
  const [callModal,    setCallModal]    = useState(false);
  const [callLead,     setCallLead]     = useState(null);

  const handleCall = (lead) => {
    setCallLead(lead);
    setCallModal(true);
  };

  const handleViewDetail = (lead) => {
    setSelectedLead(lead);
    setActiveView('lead-detail');
  };

  const handleNavClick = (key) => {
    const needsLead = key === 'call-history' || key === 'lead-detail';
    if (needsLead && !selectedLead) return;
    setActiveView(key);
  };

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: 'calc(100vh - 112px)' }}>

      {/* Sub-nav sidebar */}
      <div style={{
        width: 162, flexShrink: 0,
        background: '#f8faf8',
        borderRight: '1px solid #e8e8e8',
        borderRadius: '10px 0 0 10px',
        padding: '16px 0',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '0 14px 10px', fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.7 }}>
          <ContactsOutlined style={{ marginRight: 5 }} />CRM
        </div>

        {CRM_NAV.map((item) => {
          const isActive  = activeView === item.key;
          const needsLead = item.key === 'call-history' || item.key === 'lead-detail';
          const disabled  = needsLead && !selectedLead;

          return (
            <Tooltip key={item.key} title={disabled ? 'Select a lead first' : ''} placement="right">
              <div
                onClick={() => handleNavClick(item.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 14px',
                  margin: '1px 8px',
                  borderRadius: 8,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  background: isActive ? '#e6f4ea' : 'transparent',
                  color: isActive ? PRIMARY : disabled ? '#ccc' : '#555',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 13,
                  transition: 'background 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive && !disabled) e.currentTarget.style.background = '#eef5ee';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: 15 }}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            </Tooltip>
          );
        })}

        {selectedLead && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ padding: '0 14px' }}>
              <div style={{ fontSize: 10, color: '#aaa', marginBottom: 3, textTransform: 'uppercase', fontWeight: 600 }}>
                Selected
              </div>
              <div style={{ fontWeight: 600, fontSize: 12, color: PRIMARY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedLead.name}
              </div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{selectedLead.mobile}</div>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, fontSize: 11, height: 'auto' }}
                onClick={() => { setSelectedLead(null); setActiveView('hot-leads'); }}
              >
                Clear
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 20,
        background: '#fff',
        borderRadius: '0 10px 10px 0',
        border: '1px solid #e8e8e8',
        borderLeft: 'none',
      }}>
        {activeView === 'hot-leads'    && <HotLeadsView    onCall={handleCall} onViewDetail={handleViewDetail} />}
        {activeView === 'follow-ups'   && <FollowUpsView   onCall={handleCall} />}
        {activeView === 'call-history' && <CallHistoryView lead={selectedLead} />}
        {activeView === 'lead-detail'  && <LeadDetailView  lead={selectedLead} onCall={handleCall} />}
      </div>

      <CallModal
        open={callModal}
        lead={callLead}
        onClose={() => setCallModal(false)}
        onSuccess={() => {}}
      />
    </div>
  );
}
